// ExportChat.js — Export transkrip chat yang SEDANG TERBUKA ke XLSX / JSON.
//
// Beda mendasar dari pages/AIChatHistory.js: di sana export menarik dari server
// (`/api/ai/chat-history/export`) karena yang diekspor adalah ribuan baris lintas
// sesi. Di sini sumbernya array `messages` di memori — sesi yang sedang berjalan.
//
// Itu disengaja, bukan jalan pintas:
//   1. Sesi AI Coding bisa BELUM tersimpan seluruhnya saat user ingin mengekspor
//      (saveChatMessage berjalan asinkron, dan pesan `error` memang tidak pernah
//      dikirim ke server). Menarik dari server akan diam-diam kehilangan bagian
//      yang terlihat di layar.
//   2. Beberapa field hanya hidup di klien — `tokensEstimated`, `cached`,
//      `answerMode`, `tableRows` — dan tidak punya kolom di ai_chat_history.
//
// Konsekuensinya: yang diekspor adalah PERSIS yang terlihat di layar. Kalau user
// membuka sesi lama lewat sidebar, `messages` sudah berisi transkrip itu, jadi
// tetap benar.
import * as XLSX from 'xlsx';

// Sama persis dengan pages/AIChatHistory.js:17 — nama berkas export seragam
// di seluruh aplikasi.
function exportStamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

// Nama berkas ikut nama dashboard supaya export dari beberapa halaman tidak
// saling menimpa di folder Downloads. Karakter terlarang NTFS/ext4 dibuang.
function safeSlug(s) {
  return String(s || 'chat').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'chat';
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Batas keras panjang satu sel Excel. Melebihi ini membuat file yang ditulis
// TIDAK bisa dibuka sama sekali — jadi dipotong, bukan dibiarkan.
// Jawaban mode Coding rutin melewatinya.
const CELL_CAP = 32767;

function capCell(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  const s = typeof v === 'string' ? v : String(v);
  return s.length > CELL_CAP ? s.slice(0, CELL_CAP - 1) + '…' : s;
}

function baseMeta({ dashboardName, provider, model, sessionId, messages }) {
  const real = messages.filter(m => m.role !== 'error');
  const sum  = (k) => real.reduce((a, m) => a + (Number(m[k]) || 0), 0);
  return {
    exported_at:   new Date().toISOString(),
    dashboard:     dashboardName || '',
    provider:      provider || '',
    model:         model || '',
    session_id:    sessionId || '',
    message_count: real.length,
    tokens_prompt:     sum('promptTokens'),
    tokens_completion: sum('completionTokens'),
    tokens_total:      sum('totalTokens'),
  };
}

/**
 * XLSX: dua sheet.
 *  - "Chat"  : satu baris per pesan, kolom rata (mudah difilter di Excel)
 *  - "Info"  : metadata sesi + total token
 * `tableRows` SENGAJA tidak dibongkar jadi sheet sendiri: bentuk kolomnya
 * berbeda-beda tiap pesan, dan menggabungkannya akan merusak header. Untuk
 * kesetiaan penuh, pakai export JSON.
 */
export function exportChatToXLSX({ messages, dashboardName, provider, model, sessionId }) {
  const rows = Array.isArray(messages) ? messages : [];
  if (!rows.length) return false;

  // Header Inggris — konvensi export yang sama dengan AIChatHistory.js.
  const HEADERS = ['no', 'role', 'content', 'answer_mode', 'response_time_ms',
    'tokens_prompt', 'tokens_completion', 'tokens_total', 'tokens_estimated',
    'cached', 'has_data', 'data_source', 'row_count', 'sql'];

  const aoa = [HEADERS];
  rows.forEach((m, i) => {
    aoa.push([
      i + 1,
      capCell(m.role),
      capCell(m.content),
      capCell(m.answerMode),
      // MILIDETIK, bukan detik: `responseTime = Date.now() - startTime`
      // (AIChat.js:1058) dan UI membaginya di ResponseTimeBadge. Kolom sempat
      // dinamai `response_time_s` — 44024 terbaca sebagai 12 jam, bukan 44 detik.
      m.responseTime ?? '',
      m.promptTokens ?? '',
      m.completionTokens ?? '',
      m.totalTokens ?? '',
      capCell(m.tokensEstimated),
      capCell(m.cached),
      capCell(m.hasData),
      capCell(m.dataSource),
      Array.isArray(m.tableRows) ? m.tableRows.length : '',
      capCell(m.aiServiceSql),
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Lebar kolom: tanpa ini kolom `content` selebar satu karakter dan file
  // terasa rusak saat dibuka.
  ws['!cols'] = [{ wch: 4 }, { wch: 10 }, { wch: 80 }, { wch: 12 }, { wch: 8 },
    { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 7 }, { wch: 7 },
    { wch: 16 }, { wch: 8 }, { wch: 60 }];

  const meta = baseMeta({ dashboardName, provider, model, sessionId, messages: rows });
  const wsInfo = XLSX.utils.aoa_to_sheet(Object.entries(meta).map(([k, v]) => [k, v]));
  wsInfo['!cols'] = [{ wch: 20 }, { wch: 46 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Chat');
  XLSX.utils.book_append_sheet(wb, wsInfo, 'Info');
  XLSX.writeFile(wb, `ai-chat-${safeSlug(dashboardName)}-${exportStamp()}.xlsx`);
  return true;
}

/**
 * JSON: kesetiaan penuh — termasuk `tableRows` (hasil query per pesan) dan
 * `sources` (rujukan RAG) yang tidak muat di tabel rata.
 * Field internal render (`_animate`) dibuang; ia tidak punya arti di luar UI.
 */
export function exportChatToJSON({ messages, dashboardName, provider, model, sessionId }) {
  const rows = Array.isArray(messages) ? messages : [];
  if (!rows.length) return false;

  const payload = {
    ...baseMeta({ dashboardName, provider, model, sessionId, messages: rows }),
    messages: rows.map((m, i) => {
      const { _animate, ...rest } = m;
      return { no: i + 1, ...rest };
    }),
  };

  triggerDownload(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    `ai-chat-${safeSlug(dashboardName)}-${exportStamp()}.json`
  );
  return true;
}
